import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Checkbox from 'components/Checkbox'

import ListItem from 'pages/DeviceSearch/MultipleDevicesResults/ListItem'

import { useSessionContext } from 'hooks/SessionContext'

import { screenSize } from 'styles/shared'
import { useFeaturesContext } from 'hooks/FeaturesContext'

const propTypes = {
  pathRoot: PropTypes.string.isRequired,
  devices: PropTypes.arrayOf(PropTypes.shape)
}

const defaultProps = {
  devices: []
}

const List = ({ pathRoot, devices }) => {
  const { session: { isTechnician } } = useSessionContext()
  const storageId = pathRoot + 'Selected'

  const navigate = useNavigate()

  const { enabledFeatures } = useFeaturesContext()
  let storedSelectedDeviceIds = localStorage.getItem(storageId)
  storedSelectedDeviceIds = useMemo(() => storedSelectedDeviceIds ? storedSelectedDeviceIds.split(',') : [], [storedSelectedDeviceIds])

  const [selectedDeviceIds, setSelectedDeviceIds] = useState(_.intersection(devices.map(device => device.deviceId), storedSelectedDeviceIds))

  const onChangeSelectAll = () => {
    if (devices.length !== selectedDeviceIds.length) {
      setSelectedDevicesIdsInStateAndLocalStorage(devices.map(device => device.deviceId))
    } else {
      setSelectedDevicesIdsInStateAndLocalStorage([])
    }
  }

  const setSelectedDevicesIdsInStateAndLocalStorage = useCallback(newSelectedDeviceIds => {
    localStorage.setItem(storageId, newSelectedDeviceIds.join(','))

    setSelectedDeviceIds(newSelectedDeviceIds)
  }, [storageId, setSelectedDeviceIds])

  useEffect(() => {
    const validSelectedDeviceIds = devices
      .map((device) => device.deviceId)
      .filter((deviceId) => selectedDeviceIds.includes(deviceId))

    setSelectedDevicesIdsInStateAndLocalStorage(validSelectedDeviceIds)
  }, [devices]) // eslint-disable-line react-hooks/exhaustive-deps


 const getDeviceLink = (deviceId) => {
    if (enabledFeatures.includes('system_dashboard')) {
      return `${deviceId}/dashboard`
    } else {
      return `${deviceId}`
    }
  }

  return (
    <>
      <S.List>
        <S.SelectAllRow {...{ isTechnician }}>
          <S.SelectAllCheckbox
            label="SELECT ALL"
            onChange={onChangeSelectAll}
            checked={!_.difference(devices.map(device => device.deviceId), selectedDeviceIds).length}
          />

          {!isTechnician && <S.NotesColumnHeader>Notes</S.NotesColumnHeader>}
        </S.SelectAllRow>

        {devices.map(({ deviceId, serialNumber, note, dealerName, dealerCode }) => (
          <ListItem
            pathRoot={pathRoot}
            key={deviceId}
            deviceId={deviceId}
            getDeviceLinkCallback={getDeviceLink}
            updateSelectedDevicesIdsCallback={setSelectedDevicesIdsInStateAndLocalStorage}
            dealerPhoneNumber={dealerCode}
            {...{ note, dealerName, selectedDeviceIds, storageId, serialNumber }}
          />
        ))}
      </S.List>
      <S.ButtonContainer>
        <Button decoration="secondary"
          text="View Selected Devices"
          enabled={selectedDeviceIds.length}
          onPress={() => {
            navigate(
              `/${pathRoot}/multiple/${getDeviceLink(selectedDeviceIds[0])}?deviceIds=${encodeURIComponent(selectedDeviceIds.join(','))}`,
              { state: { selectedDevicesStorageId: storageId } }
            )
          }}
        />
      </S.ButtonContainer>
    </>
  )
}

List.propTypes = propTypes
List.defaultProps = defaultProps

export default List

const S = {
  List: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isTechnician'].includes(prop)
  })`
    margin: ${({ isTechnician }) => isTechnician ? '35px' : '35px 0 25px 0'};

    @media ${screenSize.medium} {
      margin-left: 0px;
    }
  `,
  NotesColumnHeader: styled.span`
    @media ${screenSize.medium} {
      display: none;
    }
  `,
  SelectAllRow: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isTechnician'].includes(prop)
  })`
    display: grid;
    grid-template-columns: 50% 50%;
    font-weight: bold;
    margin-bottom: 15px;

    @media ${screenSize.medium} {
      margin-bottom: ${({ isTechnician }) => isTechnician ? '15px' : '18px'};
    }
  `,
  SelectAllCheckbox: styled(Checkbox)`
    margin-left: 30px;
    height: 20px;

    > label {
      font-weight: 900;
    }

    @media ${screenSize.medium} {
      width: 100%;
    }
  `,
  ButtonContainer: styled.div`
    display: flex;
  `
}
